// Studio Workspace A — show + roster actions. Direct RLS-gated Supabase writes
// (no Edge Function, no service_role): shows_insert_owner / show_members_write_owner.

import { supabase } from '@/lib/supabase';

export interface ShowSummary {
  id: string;
  name: string;
  slug: string;
  tickerLines: string[];
  logoUrl: string | null;
}

// Logo sits over video: favor transparent/vector. SVG via <img> can't run script.
const ALLOWED_LOGO_MIME = ['image/png', 'image/webp', 'image/svg+xml'];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export interface ShowMemberEntry {
  memberId: string;
  displayName: string;
  username: string | null;
  avatarUrl: string | null;
  status: 'pending' | 'approved';
}

export interface CohostShow {
  showId: string;
  name: string;
  slug: string;
}

export interface ProfileMatch {
  id: string;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
}

// Mirrors room-create's slug helper: slugify(name)-<6 hex>.
function slugify(name: string): string {
  const base =
    name
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
      .replace(/-+$/g, '') || 'show';
  const hex = [...crypto.getRandomValues(new Uint8Array(3))]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `${base}-${hex}`;
}

export async function listShows(ownerId: string): Promise<ShowSummary[]> {
  const { data, error } = await supabase
    .from('shows')
    .select('id, name, slug, ticker_lines, logo_url')
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((s) => ({
    id: s.id,
    name: s.name,
    slug: s.slug,
    tickerLines: s.ticker_lines ?? [],
    logoUrl: s.logo_url,
  }));
}

/**
 * Upload a show logo to the owner-gated show-logos bucket and set shows.logo_url
 * (cache-busted). Mirrors SettingsPage.uploadAvatar. RLS gates the write to the
 * show owner; the bucket is public-read so the bug can render it.
 */
export async function uploadShowLogo(showId: string, file: File): Promise<string> {
  if (!ALLOWED_LOGO_MIME.includes(file.type)) throw new Error('Use a PNG, WEBP, or SVG.');
  if (file.size > MAX_LOGO_BYTES) throw new Error('Logo must be under 2 MB.');
  const ext = file.type === 'image/svg+xml' ? 'svg' : (file.name.split('.').pop() ?? 'png');
  const path = `${showId}/logo.${ext}`;
  const { error: uploadError } = await supabase.storage
    .from('show-logos')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (uploadError) throw uploadError;
  const { data: pub } = supabase.storage.from('show-logos').getPublicUrl(path);
  const url = `${pub.publicUrl}?v=${Date.now()}`;
  const { error: updateError } = await supabase
    .from('shows')
    .update({ logo_url: url })
    .eq('id', showId);
  if (updateError) throw updateError;
  return url;
}

/** Update a show's ticker template (the prep editor). RLS: shows_update_owner. */
export async function updateShowTicker(showId: string, lines: string[]): Promise<void> {
  const { error } = await supabase.from('shows').update({ ticker_lines: lines }).eq('id', showId);
  if (error) throw error;
}

export async function createShow(name: string, ownerId: string): Promise<ShowSummary> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Enter a show name');
  // Retry on a slug unique collision (regenerate); RLS gates approved-host.
  for (let attempt = 0; attempt < 3; attempt++) {
    const { data, error } = await supabase
      .from('shows')
      .insert({ owner_id: ownerId, name: trimmed, slug: slugify(trimmed) })
      .select('id, name, slug, ticker_lines, logo_url')
      .single();
    if (!error) {
      return {
        id: data.id,
        name: data.name,
        slug: data.slug,
        tickerLines: data.ticker_lines ?? [],
        logoUrl: data.logo_url,
      };
    }
    if (error.code === '23505') continue;
    if (error.code === '42501') throw new Error('Only approved hosts can create a show');
    throw error;
  }
  throw new Error('Could not create the show');
}

export async function listShowMembers(showId: string): Promise<ShowMemberEntry[]> {
  const { data, error } = await supabase
    .from('show_members')
    .select(
      'member_id, status, member:profiles!show_members_member_id_fkey(display_name, username, avatar_url)',
    )
    .eq('show_id', showId);
  if (error) throw error;
  return (data ?? []).map((r) => {
    const m = Array.isArray(r.member) ? (r.member[0] ?? null) : r.member;
    return {
      memberId: r.member_id,
      displayName: m?.display_name ?? m?.username ?? 'Member',
      username: m?.username ?? null,
      avatarUrl: m?.avatar_url ?? null,
      status: (r.status as 'pending' | 'approved') ?? 'approved',
    };
  });
}

/** The signed-in user's pending cohost invites (self rows), with show name/slug. */
export async function listMyCohostInvites(memberId: string): Promise<CohostShow[]> {
  return listMyMemberships(memberId, 'pending');
}

/** Shows the signed-in user actively cohosts (approved self rows). */
export async function listMyCohostedShows(memberId: string): Promise<CohostShow[]> {
  return listMyMemberships(memberId, 'approved');
}

async function listMyMemberships(
  memberId: string,
  status: 'pending' | 'approved',
): Promise<CohostShow[]> {
  const { data, error } = await supabase
    .from('show_members')
    .select('show_id, show:shows!show_members_show_id_fkey(name, slug)')
    .eq('member_id', memberId)
    .eq('status', status);
  if (error) throw error;
  return (data ?? []).map((r) => {
    const s = Array.isArray(r.show) ? (r.show[0] ?? null) : r.show;
    return { showId: r.show_id, name: s?.name ?? 'Show', slug: s?.slug ?? '' };
  });
}

/** Does the signed-in user have ANY show membership (pending or approved)? Drives
 *  the Studio nav un-gate for invited non-hosts. */
export async function hasAnyCohostMembership(memberId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from('show_members')
    .select('show_id', { count: 'exact', head: true })
    .eq('member_id', memberId);
  if (error) throw error;
  return (count ?? 0) > 0;
}

/** Cohost approves their own invite (self UPDATE → approved). */
export async function approveCohostInvite(showId: string, memberId: string): Promise<void> {
  const { error } = await supabase
    .from('show_members')
    .update({ status: 'approved' })
    .eq('show_id', showId)
    .eq('member_id', memberId);
  if (error) throw error;
}

/** Decline a pending invite OR stop cohosting an approved show (self DELETE). */
export async function leaveShow(showId: string, memberId: string): Promise<void> {
  const { error } = await supabase
    .from('show_members')
    .delete()
    .eq('show_id', showId)
    .eq('member_id', memberId);
  if (error) throw error;
}

// Fuzzy member search for the cohost typeahead. Partial match over username and
// display_name; same broad profiles SELECT the exact lookup used. The query is
// sanitized of PostgREST or-filter delimiters before being wrapped in wildcards.
export async function searchProfiles(rawQuery: string): Promise<ProfileMatch[]> {
  const q = rawQuery
    .trim()
    .replace(/^@/, '')
    .replace(/[,()%*]/g, ' ')
    .trim();
  if (q.length < 2) return [];
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, display_name, avatar_url')
    .or(`username.ilike.%${q}%,display_name.ilike.%${q}%`)
    .limit(8);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id,
    username: r.username,
    displayName: r.display_name,
    avatarUrl: r.avatar_url,
  }));
}

export async function addShowMemberById(
  showId: string,
  memberId: string,
  ownerId: string,
): Promise<void> {
  const { error } = await supabase
    .from('show_members')
    .insert({ show_id: showId, member_id: memberId, added_by: ownerId });
  if (error) throw error;
}

export async function removeShowMember(showId: string, memberId: string): Promise<void> {
  const { error } = await supabase
    .from('show_members')
    .delete()
    .eq('show_id', showId)
    .eq('member_id', memberId);
  if (error) throw error;
}
